import { useEffect, useRef, useState } from "react";
import { Shirt, Coins } from "lucide-react";
import { API } from "../utils/constants";
import StarField from "./StarField";

const MAX_ROWS = 8;

const formatUsd = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function timeAgo(iso) {
  if (!iso) return "";
  const time = new Date(iso).getTime();
  if (!Number.isFinite(time)) return "";

  const diffMs = Date.now() - time;
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: new Date(iso).getFullYear() !== new Date().getFullYear() ? "numeric" : undefined,
  }).format(new Date(iso));
}

function getOrderNumber(tx) {
  return (
    tx.order_number ||
    tx.fourthwall_friendly_id ||
    tx.friendly_id ||
    tx.fourthwall_order_id ||
    tx.order_id ||
    "Order"
  );
}

function normalizeTransaction(tx, index) {
  const orderNumber = String(getOrderNumber(tx));
  const amount = Number(
    tx.amount ?? tx.profit_usd ?? tx.contribution_usd ?? tx.profit_amount ?? 0
  );

  return {
    id:
      tx.id ??
      tx.fourthwall_order_id ??
      tx.order_id ??
      `${orderNumber}-${tx.created_at || tx.order_created_at || index}`,
    orderNumber,
    amount: Number.isFinite(amount) ? amount : 0,
    created_at: tx.created_at || tx.order_created_at || tx.date || null,
  };
}

function TransactionRow({ tx, isNew }) {
  const time = timeAgo(tx.created_at);

  return (
    <div className={`toa-ledger-row ${isNew ? "toa-ledger-row-new" : ""}`}>
      <div className="toa-ledger-icon toa-ledger-icon-in" aria-hidden="true">
        <Shirt className="toa-ico" />
      </div>

      <div className="toa-ledger-main">
        <div className="toa-ledger-label">TOA Apparel</div>
        <div className="toa-ledger-meta">
          Order #{tx.orderNumber}{time ? ` · ${time}` : ""}
        </div>
      </div>

      <div className="toa-ledger-amount toa-ledger-amount-in">
        +{formatUsd(tx.amount)}
      </div>
    </div>
  );
}

function TransactionRowSkeleton() {
  return (
    <div className="toa-ledger-row toa-ledger-row-skel">
      <div className="toa-skel toa-skel-circle" />
      <div className="toa-ledger-main">
        <div className="toa-skel toa-skel-line" style={{ width: "42%" }} />
        <div
          className="toa-skel toa-skel-line"
          style={{ width: "32%", height: 9, marginTop: 7 }}
        />
      </div>
      <div className="toa-skel toa-skel-line" style={{ width: 55, height: 14 }} />
    </div>
  );
}

export default function CharityFundPage() {
  const [fundTotal, setFundTotal] = useState(0);
  const [displayTotal, setDisplayTotal] = useState(0);
  const displayedRef = useRef(0);
  const animationFrame = useRef(null);

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [feedAvailable, setFeedAvailable] = useState(true);
  const [newIds, setNewIds] = useState(() => new Set());
  const knownIds = useRef(new Set());

  // Live fund total — refreshed every 30 seconds.
  useEffect(() => {
    let cancelled = false;

    async function loadTotal() {
      try {
        const response = await fetch(`${API}/merch/charity/total`, {
          headers: { Accept: "application/json" },
          credentials: "include",
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        const total = Number(data?.total || 0);
        if (!cancelled && Number.isFinite(total)) {
          setFundTotal(Math.max(0, total));
        }
      } catch (err) {
        console.error("[Charity Fund] total", err);
      }
    }

    loadTotal();
    const interval = window.setInterval(loadTotal, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // Smoothly animate the large total whenever the backend value changes.
  useEffect(() => {
    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);

    const from = displayedRef.current;
    const to = fundTotal;
    const duration = 1500;
    const startedAt = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = from + (to - from) * eased;
      displayedRef.current = value;
      setDisplayTotal(value);

      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        displayedRef.current = to;
        setDisplayTotal(to);
      }
    };

    animationFrame.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, [fundTotal]);

  function applyIncoming(list) {
    const positive = list.filter((tx) => Number(tx.amount) > 0).slice(0, MAX_ROWS);
    const fresh = positive.filter((tx) => !knownIds.current.has(String(tx.id)));

    positive.forEach((tx) => knownIds.current.add(String(tx.id)));
    setTransactions(positive);

    if (fresh.length) {
      setNewIds((prev) => {
        const next = new Set(prev);
        fresh.forEach((tx) => next.add(String(tx.id)));
        return next;
      });

      window.setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          fresh.forEach((tx) => next.delete(String(tx.id)));
          return next;
        });
      }, 2300);
    }
  }

  // Incoming charity income. The backend should return one row per Fourthwall order,
  // with all eligible Qadr/charity line profits summed into that order's amount.
  useEffect(() => {
    let cancelled = false;

    async function loadTransactions() {
      try {
        const response = await fetch(
          `${API}/merch/charity/transactions?limit=${MAX_ROWS}`,
          {
            headers: { Accept: "application/json" },
            credentials: "include",
          }
        );

        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        const raw = Array.isArray(data?.transactions) ? data.transactions : [];
        const list = raw.map(normalizeTransaction);

        if (!cancelled) {
          setFeedAvailable(true);
          setTxLoading(false);
          applyIncoming(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[Charity Fund] transactions", err);
          setFeedAvailable(false);
          setTxLoading(false);
          setTransactions([]);
        }
      }
    }

    loadTransactions();
    const interval = window.setInterval(loadTransactions, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return (
    <main className="toa-charity-page">
      <StarField />
      <div className="toa-charity-nebula" />

      <section className="toa-charity-hero">
        <div className="toa-charity-hero-inner">
          <div className="toa-charity-eyebrow">The Ones Above</div>
          <h1>Charity Fund</h1>
          <p className="toa-charity-sub">
            A community fund dedicated to turning collective support into
            meaningful help for people and causes that need it.
          </p>

          <div className="toa-charity-total-card">
            <div className="toa-charity-total">{formatUsd(displayTotal)}</div>
            <div className="toa-charity-total-caption">raised so far, together</div>
          </div>
        </div>
      </section>

      <section className="toa-ledger-flow">
        <div className="toa-ledger-head">
          <span className={`toa-ledger-dot ${feedAvailable ? "" : "toa-ledger-dot-off"}`} />
          <span className="toa-ledger-head-label">Public Ledger</span>
        </div>
        <p className="toa-ledger-description">
          We believe in transparency, so you can see where the Charity Fund’s contributions are coming from.
        </p>

        <div className="toa-ledger-list">
          {txLoading && (
            <>
              <TransactionRowSkeleton />
              <TransactionRowSkeleton />
              <TransactionRowSkeleton />
            </>
          )}

          {!txLoading && transactions.length === 0 && (
            <div className="toa-ledger-empty">
              <Coins className="toa-ico-xl" />
              <div>
                {feedAvailable
                  ? "No fund income to show yet."
                  : "Fund activity is temporarily unavailable."}
              </div>
            </div>
          )}

          {!txLoading &&
            transactions.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                isNew={newIds.has(String(tx.id))}
              />
            ))}
        </div>
      </section>
    </main>
  );
}
